import React from "react";
import { EyeOff, Flag } from "lucide-react";
import StarRating from "../../reviews/components/StarRating";
import { fmtAvg, fmtDate } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";
import ReplyEditor from "./ReplyEditor";

/**
 * ONE review as the practice sees it: the public row plus its moderation state.
 *
 * TWO PILLS, TWO DIFFERENT FACTS, and neither is the other's synonym:
 *   · HIDDEN — the platform operator took it off the public page. It still counts for
 *     nothing in the rating (the summaries are recomputed from visible rows only) and it is
 *     still shown here, because a practitioner is owed the record of what was said.
 *   · FLAGGED — the practice ASKED for a look. It is a request, not an outcome: a flagged
 *     review stays public until an operator decides otherwise, and the pill says only that
 *     the ask was made. One flag per review; once raised, the button is gone rather than
 *     disabled, so nothing offers an action that would only 409.
 *
 * @param {object} review an `owner_review` row
 * @param {function} t `useT("admin")`
 * @param {function} tv `useT("common")`
 * @param {string} lang
 * @param {() => void} onChanged re-read the list after a write landed
 * @param {() => void} onFlag open the page's single flag dialog on this row
 */
export default function ReviewRow({ review, t, tv, lang, onChanged, onFlag }) {
  return (
    <li className="list-none px-5 py-4" data-testid={REVIEWS.adminRow(review.id)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <StarRating
          value={review.rating}
          readOnly
          variant="light"
          size="sm"
          label={t("rev.row.aria", { avg: fmtAvg(review.rating) })}
        />
        <span className="text-sm font-medium text-eh-ink min-w-0 truncate">{review.reviewer_display}</span>
        <span className="text-xs text-eh-muted tabular-nums">{fmtDate(review.created_at, lang)}</span>
        {review.service_name && (
          <span className="text-xs text-eh-muted min-w-0 truncate">{review.service_name}</span>
        )}
        {review.hidden && (
          <span
            data-testid={REVIEWS.adminHiddenPill(review.id)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 border-slate-200"
          >
            <EyeOff className="w-3 h-3" /> {t("rev.row.hidden")}
          </span>
        )}
        {review.flag && (
          <span
            data-testid={REVIEWS.adminFlaggedPill(review.id)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
          >
            <Flag className="w-3 h-3" /> {t("rev.row.flagged")}
          </span>
        )}
      </div>

      {review.text && (
        <p className="text-sm text-eh-ink/80 mt-2 whitespace-pre-line break-words">{review.text}</p>
      )}

      <ReplyEditor review={review} t={t} tv={tv} lang={lang} onReplied={onChanged} />

      {!review.flag && (
        <button
          type="button"
          onClick={onFlag}
          data-testid={REVIEWS.adminFlagButton(review.id)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-eh-muted hover:text-amber-800 underline"
        >
          <Flag className="w-3 h-3" /> {t("rev.flag.button")}
        </button>
      )}
    </li>
  );
}
